import React, {
	Dispatch,
	SetStateAction,
	RefObject,
	PropsWithChildren,
	createContext,
	useContext,
	useState,
	useEffect,
	useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Toast } from 'primereact/toast';

type OpenWindow = {
	window: Window;
};

type GlobalContextValue = {
	toastRef: RefObject<Toast>;
	reportIssueModalIsOpen: boolean;
	setReportIssueModalIsOpen: Dispatch<SetStateAction<boolean>>;
	avatarModalIsOpen: boolean;
	setAvatarModalIsOpen: Dispatch<SetStateAction<boolean>>;
	myAccountModalOpen: boolean;
	setMyAccountModalOpen: Dispatch<SetStateAction<boolean>>;
	myCalendarModalOpen: boolean;
	setMyCalendarModalOpen: Dispatch<SetStateAction<boolean>>;
	newChats: number[];
	setNewChats: Dispatch<SetStateAction<number[]>>;
	openWindows: Record<string, OpenWindow[]>;
	setOpenWindows: Dispatch<SetStateAction<Record<string, OpenWindow[]>>>;
	closedWindowsCounts: Record<string, number>;
	prevPathname: string | null;
};

const GlobalContext = createContext<GlobalContextValue>({} as GlobalContextValue);

// eslint-disable-next-line @typescript-eslint/ban-types
type GlobalContextProviderProps = PropsWithChildren<{
	value: {
		toastRef: RefObject<Toast>;
	};
}>;

export const GlobalContextProvider = ({ value, children }: GlobalContextProviderProps) => {
	const { toastRef } = value;
	const [reportIssueModalIsOpen, setReportIssueModalIsOpen] = useState(false);
	const [avatarModalIsOpen, setAvatarModalIsOpen] = useState(false);
	const [myAccountModalOpen, setMyAccountModalOpen] = useState(false);
	const [myCalendarModalOpen, setMyCalendarModalOpen] = useState(false);
	const [newChats, setNewChats] = useState<number[]>([]);
	const [openWindows, setOpenWindows] = useState<Record<string, OpenWindow[]>>({});
	const [closedWindowsCounts, setClosedWindowsCounts] = useState<Record<string, number>>({});

	const { pathname } = useLocation();
	const prevPathname = useRef<string | null>(null);

	useEffect(() => {
		prevPathname.current = pathname;
	}, [pathname]);

	useEffect(() => {
		Object.keys(openWindows).forEach((key) => {
			openWindows[key].forEach((openWindow) => {
				const checkClosed = setInterval(() => {
					if (openWindow.window.closed) {
						setClosedWindowsCounts((prev) => ({
							...prev,
							[key]: (prev[key] || 0) + 1,
						}));
						setOpenWindows((prev) => ({
							...prev,
							[key]: prev[key].filter((w) => w !== openWindow),
						}));
						clearInterval(checkClosed);
					}
				}, 1000);
			});
		});
	}, [openWindows]);

	// open my account dialog after open page
	const { search } = useLocation();
	useEffect(() => {
		const searchParams = new URLSearchParams(search);
		if (searchParams.get('dialog') === 'myAccount') {
			setTimeout(() => {
				setMyAccountModalOpen(true);
				setTimeout(() => {
					searchParams.delete('dialog');
					const newSearch = searchParams.toString();
					const newUrl = `${window.location.pathname}${newSearch.length ? `?${newSearch}` : ''}`;
					window.history.replaceState({}, '', newUrl);
				}, 100);
			}, 1500);
		}
	}, [search]);

	return (
		<GlobalContext.Provider
			value={{
				toastRef,
				reportIssueModalIsOpen,
				setReportIssueModalIsOpen,
				avatarModalIsOpen,
				setAvatarModalIsOpen,
				myAccountModalOpen,
				setMyAccountModalOpen,
				myCalendarModalOpen,
				setMyCalendarModalOpen,
				newChats,
				setNewChats,
				openWindows,
				setOpenWindows,
				closedWindowsCounts,
				prevPathname: prevPathname.current,
			}}
		>
			{children}
		</GlobalContext.Provider>
	);
};

export const useGlobal = () => useContext(GlobalContext);
